import { z } from 'zod/v4';
import { ASSIGNMENT_PROVENANCE_VALUES } from './order-logic/efiling/assignment-provenance.js';
export const FilingTypeSchema = z.enum([
    'complaint',
    'answer',
    'motion',
    'reply',
    'opposition',
    'declaration',
    'stipulation',
    'notice',
    'subpoena',
    'proof-of-service',
    'judgment',
    'order',
    'writ',
    'petition',
    'response',
    'other',
]);
export const FilingStatusSchema = z.enum([
    'draft',
    'pending',
    'submitted',
    'processing',
    'under-review',
    'accepted',
    'rejected',
    'payment-pending',
    'payment-failed',
    'filed',
]);
export const DocumentSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    fileName: z.string(),
    mimeType: z.string(),
    size: z.number(),
    url: z.string().url().optional(),
    uploadedAt: z.string().datetime(),
});
// eFiling document type reference (from EFM taxonomy)
export const EFilingDocumentTypeRefSchema = z.object({
    code: z.string(),
    name: z.string(),
});
// eFiling document title reference
export const EFilingDocumentTitleRefSchema = z.object({
    code: z.string(),
    name: z.string(),
});
// Expanded eFiling document schema
export const EFilingDocumentSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    fileName: z.string(),
    mimeType: z.string(),
    size: z.number(),
    url: z.string().url().optional(),
    uploadedAt: z.string().datetime(),
    // eFiling-specific fields
    efileKey: z.string().optional(),
    documentType: EFilingDocumentTypeRefSchema.optional(),
    documentTitle: EFilingDocumentTitleRefSchema.optional(),
    isLeadDocument: z.boolean().default(false),
    /** JTI formGroups: EFCI_LEAD (CI lead), EFCI (CI connected), EF_LEAD (sub lead) */
    formGroups: z.array(z.string()).optional(),
    metadataValues: z.record(z.string(), z.unknown()).default({}),
    /**
     * Per-slot assignment provenance (SPEC-SOR-220-v2), keyed by party-class
     * code. Deliberately OPTIONAL with NO default (the `sectionStatuses`
     * discipline): absent IS "nothing recorded", so a draft saved before
     * provenance existed parses unchanged and no default spread can clobber a
     * persisted map. Read only through `getAssignmentProvenance`; written only
     * through `nextAssignmentProvenance`.
     */
    assignmentProvenance: z.record(z.string(), z.enum(ASSIGNMENT_PROVENANCE_VALUES)).optional(),
    securityLevel: z.string().optional(),
    isPrivate: z.boolean().default(false),
    pageCount: z.number().optional(),
});
export const FilingSchema = z.object({
    id: z.string().uuid(),
    orderId: z.string().uuid(),
    caseId: z.string().uuid().optional(),
    caseNumber: z.string().optional(),
    type: FilingTypeSchema,
    status: FilingStatusSchema,
    court: z.string(),
    courtLocation: z.string().optional(),
    jurisdiction: z.enum(['state', 'federal']),
    state: z.string().optional(),
    documents: z.array(DocumentSchema),
    filingFee: z.number().optional(),
    serviceFee: z.number().optional(),
    totalFee: z.number().optional(),
    confirmationNumber: z.string().optional(),
    envelopeId: z.string().optional(),
    filedAt: z.string().datetime().optional(),
    rejectionReason: z.string().optional(),
    notes: z.string().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    userId: z.string().uuid(),
    // eFiling documents (separate from legacy documents)
    efilingDocuments: z.array(EFilingDocumentSchema).optional(),
});
export const CreateFilingSchema = FilingSchema.omit({
    id: true,
    confirmationNumber: true,
    envelopeId: true,
    filedAt: true,
    rejectionReason: true,
    createdAt: true,
    updatedAt: true,
});
export const UpdateFilingSchema = FilingSchema.partial().omit({
    id: true,
    orderId: true,
    createdAt: true,
    userId: true,
});
